// Copy used in more than one place. One-off text stays next to its component.

export const COMMON = {
  loading: 'Memuat…',
  processing: 'sedang diproses',
  refreshing: 'Memperbarui…',
  retry: 'Coba lagi',
  cancel: 'Batal',
  back: 'Kembali',
  close: 'Tutup',
  continue: 'Lanjutkan',
  clearFilters: 'Hapus filter',
  showLess: 'Ringkas',
  backHome: 'Kembali ke beranda',
} as const;

/** Manual entry when a reader does not read a tag (load and unload). */
export const MANUAL_SERIAL = {
  required: 'Isi nomor seri yang tercetak di tabung.',
  placeholder: 'CNG-000123',
  label: 'Nomor seri',
} as const;

/** Marking a missing cylinder as found (cylinder page and Surat Jalan page). */
export const FOUND = {
  confirmTitle: (serial: string) => `Tandai ${serial} ditemukan?`,
  confirmLabel: 'Tandai ditemukan',
  done: (serial: string, location: string) => `${serial} ditandai ditemukan di ${location}`,
  /** when the server names no place */
  somewhere: 'lokasi terakhir terbaca',
  /** the confirmation: where it will be recorded, from the last Reader read (its time when known) */
  confirmBody: (place: string, at: string | null) => `Tabung akan dicatat berada di ${place}, sesuai pembacaan Reader terakhir${at ? ` (${at})` : ''}.`,
} as const;

/** Operator scan screens: a warning card that can be dismissed. */
export const DISMISS_WARNING = 'Tutup peringatan';

/** Operations mode (phone screens) and the titles of its screens. */
export const OPERATIONS = {
  name: 'Operasional',
  dispatch: 'Kirim Tabung',
  receive: 'Terima Kiriman',
  register: 'Registrasi Tabung',
  lookup: 'Cek Tabung',
} as const;

/** Live connection lost (no internet, or the live stream has been down for a while). */
export const CONNECTION = {
  lost: 'Koneksi terputus. Data di layar mungkin belum terbaru; menyambung ulang otomatis.',
  operationsLost: 'Koneksi putus, pemindaian tidak tampil langsung. Menyambung ulang…',
  /** the alert tone is on but the browser waits for one interaction before it may play */
  soundLocked: 'Bunyi belum aktif: klik atau sentuh layar sekali agar browser mengizinkan bunyi.',
} as const;

/** A list asked with a filter outside the account's region (an old link): 403 OUT_OF_SCOPE. */
export const OUT_OF_SCOPE_FILTER = {
  title: 'Filter ini di luar wilayah akses Anda',
  text: 'Tautan ini menyaring lokasi atau wilayah yang tidak bisa dibuka akun Anda. Hapus filter untuk melihat data di wilayah Anda.',
} as const;

/** A known page this account may not open (its role lacks the permission): the 403 state. */
export const FORBIDDEN = {
  title: 'Akses ditolak',
  text: 'Akun Anda tidak memiliki izin untuk membuka halaman ini. Hubungi administrator bila Anda memerlukan akses.',
} as const;

export const NOT_FOUND = {
  title: 'Halaman tidak ditemukan',
  text: 'Alamat ini tidak ada atau sudah dipindahkan. Periksa lagi tautannya.',
  /** a record by id: gone, or outside the account's region (the server does not say which) */
  record: 'Data ini sudah dihapus, tautannya keliru, atau berada di luar wilayah akses akun Anda.',
} as const;

export const PAGE_FAILED = {
  title: 'Halaman ini gagal ditampilkan',
  text: 'Terjadi kesalahan saat membuka halaman ini. Muat ulang untuk mencoba lagi; data Anda tidak hilang.',
  reload: 'Muat ulang',
} as const;

/** A map inside a page: the mouse wheel scrolls the page, zooming needs the modifier key. */
export const MAP_WHEEL_HINT = (apple: boolean) => `Tahan ${apple ? '⌘' : 'Ctrl'} lalu scroll untuk zoom`;

/** Location photos: the gallery on the location page, uploads, the cover. */
export const PHOTOS = {
  title: 'Foto lokasi',
  empty: 'Belum ada foto lokasi',
  emptyAdmin: 'Tambahkan foto tampak depan, papan nama, atau tempat tabung disimpan. Foto pertama menjadi sampul.',
  emptyViewer: 'Foto lokasi ditambahkan oleh admin.',
  /** the gallery's one muted line for an account that cannot add photos */
  noneYet: 'Belum ada foto lokasi.',
  more: (n: number) => `+${n}`,
  moreLabel: (n: number) => `Lihat ${n} foto lainnya`,
  showAll: (n: number) => `Lihat semua (${n})`,
  showLess: 'Ringkas',
  count: (n: number, max: number) => `${n} dari ${max} foto`,
  add: 'Tambah foto',
  addFirst: 'Tambah foto lokasi',
  pick: 'pilih dari perangkat',
  dropHint: 'JPEG, PNG, atau WebP. Foto besar diperkecil otomatis.',
  dropActive: 'Lepaskan untuk mengunggah',
  full: (max: number) => `Galeri sudah berisi ${max} foto, batas per lokasi. Hapus satu foto untuk menambah yang baru.`,
  waiting: 'Menunggu…',
  preparing: 'Menyiapkan…',
  uploading: (pct: number) => `Mengunggah ${pct}%`,
  done: 'Terunggah',
  added: (n: number) => `${n} foto ditambahkan`,
  uploadsLabel: 'Unggahan foto',
  cover: 'Sampul',
  makeCover: 'Jadikan sampul',
  coverSet: 'Sampul lokasi diganti',
  moveLeft: 'Geser ke kiri',
  moveRight: 'Geser ke kanan',
  movedTo: (i: number, n: number) => `Foto dipindah ke posisi ${i} dari ${n}`,
  remove: 'Hapus foto',
  removeTitle: 'Hapus foto ini?',
  removeBody: 'Foto dihapus dari galeri lokasi ini dan tidak bisa dikembalikan.',
  removeCoverBody: 'Foto ini sampul lokasi. Setelah dihapus, foto lain atau ilustrasi lokasi dipakai sebagai sampul. Foto tidak bisa dikembalikan.',
  removed: 'Foto dihapus',
  actions: (i: number) => `Tindakan foto ${i}`,
  open: (i: number, n: number) => `Buka foto ${i} dari ${n}`,
  openCover: 'Buka foto sampul',
  loadFailed: 'Foto tidak bisa dimuat',
  reloadImage: 'Muat ulang foto',
  dismiss: 'Tutup pesan',
  cancel: (name: string) => `Batalkan unggahan ${name}`,
  dragLead: 'Tarik foto ke sini, atau',
  errors: {
    invalid: 'Bukan foto JPEG, PNG, atau WebP yang bisa dibaca.',
    duplicate: 'Foto ini sudah ada di galeri.',
    limit: (max: number) => `Batas ${max} foto per lokasi sudah tercapai.`,
    tooLarge: 'Ukuran foto terlalu besar.',
  },
} as const;

/** Full-screen photo viewer. */
export const LIGHTBOX = {
  label: 'Penampil foto',
  prev: 'Foto sebelumnya',
  next: 'Foto berikutnya',
  close: 'Tutup penampil foto',
  counter: (i: number, n: number) => `${i} / ${n}`,
  failed: 'Foto tidak bisa dimuat.',
} as const;

/** Popup filters above a list (FilterMenu, FilterChips, FilterSheetButton). */
/** The screen states every page shares (ui/States.tsx); a page passes only its own sentence. */
export const STATES = {
  /** a list or a view still loading after a few seconds */
  slow: 'Koneksi lambat, masih memuat…',
  offlineTitle: 'Tidak ada koneksi internet',
  errorTitle: 'Data tidak dapat dimuat',
  sessionExpired: { title: 'Sesi Anda berakhir', text: 'Masuk lagi untuk melanjutkan. Halaman yang terakhir dibuka akan tampil kembali.' },
} as const;

export const FILTERS = {
  button: 'Filter',
  /** the badge on the phone's Filter button, read out */
  activeCount: (n: number) => `${n} filter aktif`,
  sheetTitle: 'Filter',
  showResults: 'Lihat hasil',
  /** the sheet's close button once the list behind it has answered (an exact count) */
  showCount: (n: string) => `Lihat ${n} hasil`,
  noResults: 'Tidak ada hasil',
  active: 'Filter aktif',
  clearAll: 'Hapus semua',
  /** the sheet's section of the yes/no filters */
  more: 'Lainnya',
  remove: (label: string) => `Hapus filter ${label}`,
} as const;

/** Range calendar (RangeCalendar). */
export const CALENDAR = {
  prevMonth: 'Bulan sebelumnya',
  nextMonth: 'Bulan berikutnya',
  pickStart: 'Pilih tanggal awal',
  pickEnd: 'Pilih tanggal akhir, atau tanggal yang sama untuk satu hari',
  today: 'hari ini',
  /** the date field's three parts, in the Indonesian order (tanggal, bulan, tahun) */
  day: 'Tanggal',
  /** the day select's empty choice: short, the narrowest of the three columns (its label stays "Tanggal") */
  dayShort: 'Tgl',
  month: 'Bulan',
  year: 'Tahun',
  months: ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'],
} as const;

/** Log aktivitas: the filter toolbar. */
export const AUDIT_FILTERS = {
  search: 'Cari aktivitas',
  searchPlaceholder: 'Cari email, nama, lokasi, nomor…',
  /** phones: the box shares its row with the Filter button */
  searchPlaceholderShort: 'Cari aktivitas…',
  category: 'Kategori',
  allCategories: 'Semua',
  categoriesPicked: (n: number) => `${n} dipilih`,
  user: 'Pengguna',
  allUsers: 'Semua pengguna',
  system: 'Sistem',
  systemSub: 'Impor dan perintah administrator sistem',
  userSearch: 'Cari nama atau email',
  usersEmpty: 'Tidak ada pengguna yang cocok',
  usersFailed: 'Daftar pengguna gagal dimuat.',
  searching: 'Mencari…',
  moreUsers: (n: number) => `${n} pengguna pertama. Ketik nama untuk mencari yang lain.`,
  inactive: 'nonaktif',
  /** a user id in the URL whose name is not known yet (it shows once a row of theirs is listed) */
  someUser: 'Pengguna terpilih',
  when: 'Waktu',
  allTime: 'Semua waktu',
  today: 'Hari ini',
  days: (n: number) => `${n} hari`,
  lastDays: (n: number) => `${n} hari terakhir`,
  thisMonth: 'Bulan ini',
  since: (day: string) => `Sejak ${day}`,
  until: (day: string) => `Sampai ${day}`,
  pickRange: 'Pilih rentang…',
  rangeTitle: 'Rentang tanggal (WIB)',
} as const;

/** Personal data the server hid from an account without access to it (a Pengawas). */
export const PII = {
  masked: 'Disamarkan: akun Anda tidak menampilkan data pribadi.',
  maskedShort: 'disamarkan',
} as const;

/** The distribution network: a location's parent and children, an operator's "Jaringan saya". */
export const NETWORK = {
  parent: 'Induk',
  noParent: 'Tanpa induk',
  parentHelp: (type: string, optional: boolean) => `${type} yang memasok lokasi ini${optional ? ' (boleh dikosongkan)' : ''}.`,
  parentSearch: (type: string) => `Cari ${type}…`,
  children: (n: string) => `Bawahan · ${n} lokasi`,
  childrenEmpty: 'Belum ada lokasi bawahan',
  mine: 'Jaringan saya',
  mineSub: (n: number) => `${n} lokasi dipasok dari lokasi Anda`,
  mineEmpty: 'Belum ada lokasi di jaringan Anda',
  own: 'Lokasi Anda',
  cylinders: 'tabung',
  empty: 'Kosong',
  low: 'Stok sedikit',
  noun: 'lokasi',
  hasChildren: (name: string, n: number | string) => `${name} masih menjadi induk ${n} lokasi. Pindahkan lokasi bawahannya ke induk lain dulu.`,
} as const;

/** The control room's region, fixed for a province account. */
export const SCOPE_LOCK = { title: 'Wilayah akses akun Anda', shown: 'Wilayah yang ditampilkan' } as const;

/** One file's upload state (UploadStatusChip): the public documents, the visit photos, the PKS scan. */
export const UPLOAD_STATUS = {
  preparing: (noun: string) => `Menyiapkan ${noun}…`,
  uploading: (pct: number) => `Mengunggah ${pct}%`,
  waiting: 'Menunggu koneksi, dilanjutkan otomatis',
  retrying: 'Gagal terkirim, mencoba lagi otomatis…',
  done: 'Terunggah',
  failed: 'Gagal diunggah',
  file: 'berkas',
  photo: 'foto',
} as const;
